import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { resolveConfig, describeBand } from "./riskAggregates";

/**
 * 14030 — Filter toolbar for the Questionnaire & Risk Analysis tab.
 *
 * Layout:
 *   [RISK MODEL] [▼ Diligent Risk Model · Standard]
 *     [Finding Severity ▼] [Section Risk Score ▼] [Section Criticality ▼]
 *     [Question Criticality ▼]                                    [🔍 Search]
 *
 * All four filter dropdowns are multi-select. An empty selection = "no filter
 * applied" (show everything). Search filters question text, code, response,
 * and comments. Wired via a single `filters` object so the parent can fan it
 * out to sidebar + accordion + question list in one pass.
 */

export const EMPTY_FILTERS = Object.freeze({
  severity: new Set(),
  sectionScore: new Set(),
  sectionCriticality: new Set(),
  questionCriticality: new Set(),
  search: "",
});

// Static color classes per band/tier name — the *names* come from config, but
// the Tailwind classes for each named bucket are constant FE styling. To make
// custom band names show up cleanly we fall back to neutral gray.
const PILL_BY_NAME = {
  Critical:    "bg-red-100 text-red-800 border-red-200",
  High:        "bg-orange-100 text-orange-800 border-orange-200",
  Medium:      "bg-yellow-100 text-yellow-800 border-yellow-200",
  Low:         "bg-blue-100 text-blue-800 border-blue-200",
  Compliant:   "bg-green-100 text-green-800 border-green-200",
  "Not Scored":"bg-gray-100 text-gray-700 border-gray-200",
  Major:       "bg-yellow-100 text-yellow-800 border-yellow-200",
  Minor:       "bg-emerald-100 text-emerald-800 border-emerald-200",
  Red:         "bg-red-100 text-red-800 border-red-200",
  Yellow:      "bg-yellow-100 text-yellow-800 border-yellow-200",
  Green:       "bg-green-100 text-green-800 border-green-200",
};
const pillFor = (name) => PILL_BY_NAME[name] || "bg-gray-100 text-gray-700 border-gray-200";

const VqqFiltersBar = ({
  riskModelName,
  filters,
  onChange,
  methodology = null,
  isAdmin = false,
  // 14032 AC #17 — sponsor sees an INFORMATIONAL (read-only) picker so they
  // can browse models the admin authored for their VQQ. Vendor sees only the
  // selected-model badge per AC #18. Defaults to "vendor" so callers that
  // forget to set it default to the most-restrictive behavior.
  viewerKind = "vendor",
  models = null,
  onSelectModel = null,
  previewModelId = undefined,
  isPreviewing = false,
  isRescoring = false,
}) => {
  const config = useMemo(() => resolveConfig(methodology), [methodology]);

  // Filter options derive from the runtime config — change the config row in
  // the DB and every dropdown re-labels itself on the next page load.
  const severityOptions = useMemo(() => {
    const findingTypes = (config.answer_factors || [])
      .map((a) => a.findingType)
      .filter(Boolean);
    const tierBins = (config.severity_tier_bins || []).map((b) => b.tier);
    // De-dupe + prepend Compliant, append Not Scored so the UX matches the
    // engine's "Compliant if Yes/NA, tier otherwise, Not Scored if no rubric".
    const ordered = ["Compliant", ...tierBins.filter((t) => t !== "Compliant"), "Not Scored"];
    return Array.from(new Set(ordered)).map((value) => ({
      value,
      label: value,
      pill: pillFor(value),
    }));
  }, [config]);

  const sectionScoreOptions = useMemo(
    () =>
      (config.question_rag_bands || []).map((b) => ({
        value: b.band,
        label: describeBand(b.band, config.question_rag_bands),
        pill: pillFor(b.band),
      })),
    [config]
  );

  const criticalityOptions = useMemo(
    () =>
      (config.question_criticality_rules || []).map((r) => ({
        value: r.tier,
        label: r.tier,
        pill: pillFor(r.tier),
      })),
    [config]
  );

  const update = (patch) => onChange({ ...filters, ...patch });
  const toggleIn = (key, value) => {
    const next = new Set(filters[key]);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    update({ [key]: next });
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-3 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <RiskModelPicker
          name={riskModelName}
          isAdmin={isAdmin}
          viewerKind={viewerKind}
          models={models}
          onSelect={onSelectModel}
          previewModelId={previewModelId}
          isPreviewing={isPreviewing}
          isRescoring={isRescoring}
        />
      </div>

      <div className="flex items-center gap-1 flex-wrap border-t border-gray-100 pt-3">
        <FilterDropdown
          label="Finding Severity"
          info="Severity of each individual risk finding, derived from the question's Risk Score and the vendor's response. Filter to focus on Critical / High / Medium / Low gaps or already-Compliant responses."
          options={severityOptions}
          selected={filters.severity}
          onToggle={(v) => toggleIn("severity", v)}
          onClear={() => update({ severity: new Set() })}
        />
        <FilterDropdown
          label="Section Risk Score"
          info="Each section's intrinsic risk score (1–125) using Mean(question scores) + 0.5 × sample StDev. Bands: Red ≥ 50, Yellow 20–49, Green < 20."
          options={sectionScoreOptions}
          selected={filters.sectionScore}
          onToggle={(v) => toggleIn("sectionScore", v)}
          onClear={() => update({ sectionScore: new Set() })}
        />
        <FilterDropdown
          label="Section Criticality"
          info="How dangerous a section is to ignore. Bucketed mean of per-question Criticality. Critical when mean ≥ 4, Major when 2 < mean < 4, Minor when mean ≤ 2."
          options={criticalityOptions}
          selected={filters.sectionCriticality}
          onToggle={(v) => toggleIn("sectionCriticality", v)}
          onClear={() => update({ sectionCriticality: new Set() })}
        />
        <FilterDropdown
          label="Question Criticality"
          info="Risk Model criticality of each question. Critical when Risk Score ≥ 50 OR Impact ≥ 4. Major when Risk Score 20–49 OR Impact = 3. Minor otherwise."
          options={criticalityOptions}
          selected={filters.questionCriticality}
          onToggle={(v) => toggleIn("questionCriticality", v)}
          onClear={() => update({ questionCriticality: new Set() })}
        />
        <div className="ml-auto">
          <SearchInput value={filters.search} onChange={(v) => update({ search: v })} />
        </div>
      </div>
    </div>
  );
};

/**
 * 14032 — Active model picker for the Q&RA tab.
 *
 *   - Non-admin or no options loaded → render the legacy read-only label.
 *   - Admin with the list:
 *       • Dropdown opens to show stock Diligent Risk Model + any custom
 *         models scoped to this VQQ's (template, sponsor) pair.
 *       • Choosing a different option STAGES the selection (server call) and
 *         exposes a "Rescore" button next to the picker — no auto-rescore.
 *       • Rescore button triggers POST /score/force to apply the new model.
 *
 * Falls back gracefully when admin doesn't have any custom models for the
 * scope — the dropdown still works, just with one option.
 */
export const RiskModelPicker = ({ name, isAdmin = false, viewerKind = "vendor", models, onSelect, previewModelId = undefined, isPreviewing = false, isRescoring }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // While previewing, the "active" option is the previewed model; otherwise
  // it's the VQQ's persisted selection (is_selected).
  const activeId = (m) => m.custom_risk_model_id || null;
  const selected = isPreviewing
    ? (models || []).find((m) => activeId(m) === (previewModelId ?? null)) || null
    : (models || []).find((m) => m.is_selected) || null;
  const displayName = selected?.name || name || "Diligent Risk Model";
  const displayKind = selected?.kind || "Standard";
  const optionCount = (models || []).length;
  // Plan B picker behavior by viewer. Admin and sponsor can both open the
  // dropdown and select a model to preview it (the Apply & publish / Reset
  // controls live in the preview banner above the tab); the sponsor is scoped
  // to their own VQQ. A vendor can't open the dropdown at all, they just see
  // the badge.
  const canOpen = (isAdmin || viewerKind === "sponsor") && optionCount > 1;
  const canSelect = isAdmin || viewerKind === "sponsor";

  return (
    <div className="flex items-center gap-3 w-full" ref={ref}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 shrink-0 w-24">
        Risk Model
      </span>
      <div className="relative">
        <button
          type="button"
          onClick={() => canOpen && !isRescoring && setOpen((v) => !v)}
          disabled={!canOpen || isRescoring}
          className={`inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 w-[22rem] justify-between ${
            canOpen && !isRescoring ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
          } ${isRescoring ? "opacity-80" : ""}`}
        >
          <span className="flex items-center gap-2 min-w-0 flex-1">
            {isRescoring ? (
              <svg className="h-3.5 w-3.5 animate-spin text-indigo-600 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
            ) : null}
            <span className="font-medium truncate">
              {isRescoring ? "Updating…" : displayName}
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 shrink-0">
            <KindBadge kind={displayKind} />
            {canOpen ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" /> : null}
          </span>
        </button>

        {canOpen && open && !isRescoring ? (
          <div className="absolute z-30 mt-1 w-[22rem] rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden">
            {canSelect ? (
              <p className="px-3 py-2 text-[10px] text-gray-500 border-b border-gray-100 bg-gray-50">
                Selecting previews the model. Use Apply &amp; publish in the banner to make it the official view.
              </p>
            ) : null}
            <ul className="py-1 max-h-72 overflow-y-auto">
              {(models || []).map((m) => {
                const isSelected = isPreviewing
                  ? activeId(m) === (previewModelId ?? null)
                  : !!m.is_selected;
                return (
                  <li key={m.custom_risk_model_id || "stock"}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!canSelect) return;
                        onSelect?.(m.custom_risk_model_id || null, m.name);
                        setOpen(false);
                      }}
                      disabled={!canSelect}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                        isSelected
                          ? "bg-indigo-50 text-indigo-700"
                          : canSelect
                            ? "hover:bg-gray-50 text-gray-800"
                            : "text-gray-700 cursor-default"
                      }`}
                    >
                      <span className="flex-1 min-w-0 truncate font-medium">{m.name}</span>
                      <KindBadge kind={m.kind} />
                      <span className="w-4 shrink-0 flex justify-center">
                        {isSelected ? <CheckIcon className="h-3.5 w-3.5 text-indigo-600" /> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {/* sponsors can author their own variant, deep-linked
                to the authoring page pre-scoped to this VQQ's template. */}
            {viewerKind === "sponsor" ? (
              <Link
                to={(() => {
                  const tplId = (models || []).map((m) => m.diligent_risk_template_id).find(Boolean);
                  return tplId ? `/Sponsor/RiskModels?template=${tplId}` : "/Sponsor/RiskModels";
                })()}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-[12px] font-medium text-indigo-600 hover:bg-indigo-50 border-t border-gray-100"
              >
                + Create / manage my custom models
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const KindBadge = ({ kind }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2 py-[1px] text-[10px] font-semibold whitespace-nowrap ${
      kind === "Custom"
        ? "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700"
        : "border-indigo-200 bg-indigo-50 text-indigo-700"
    }`}
  >
    {kind || "Standard"}
  </span>
);

const CheckIcon = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.704 5.296a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.29a1 1 0 011.411 0z"
      clipRule="evenodd"
    />
  </svg>
);

const FilterDropdown = ({ label, info, options, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const count = selected.size;
  const hasFilter = count > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm hover:bg-gray-50 ${
          hasFilter ? "text-indigo-700 font-medium" : "text-gray-700"
        }`}
      >
        <span>{label}</span>
        <span
          className="inline-flex items-center justify-center cursor-help"
          title={info || ""}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <InfoCircle className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
        </span>
        {hasFilter ? (
          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full bg-indigo-600 text-white text-[10px] font-semibold tabular-nums">
            {count}
          </span>
        ) : null}
        <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
      </button>

      {open ? (
        <div className="absolute z-30 mt-1 w-56 rounded-md border border-gray-200 bg-white shadow-lg p-1">
          <ul className="py-1 space-y-0.5">
            {options.map((opt) => {
              const checked = selected.has(opt.value);
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => onToggle(opt.value)}
                    className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-gray-50"
                  >
                    <Checkbox checked={checked} />
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${opt.pill}`}
                    >
                      {opt.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {hasFilter ? (
            <div className="border-t border-gray-100 mt-1 pt-1 px-2 py-1">
              <button
                type="button"
                onClick={onClear}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                Clear
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const Checkbox = ({ checked }) => (
  <span
    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
      checked ? "border-indigo-600 bg-indigo-600" : "border-gray-300 bg-white"
    }`}
  >
    {checked ? (
      <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M16.704 5.296a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.29a1 1 0 011.411 0z"
          clipRule="evenodd"
        />
      </svg>
    ) : null}
  </span>
);

const SearchInput = ({ value, onChange }) => (
  <div className="relative">
    <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-400">
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M8 4a4 4 0 100 8 4 4 0 000-8zm-6 4a6 6 0 1110.89 3.476l4.317 4.317a1 1 0 01-1.414 1.414l-4.317-4.317A6 6 0 012 8z"
          clipRule="evenodd"
        />
      </svg>
    </span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search question, response, ..."
      className="rounded-md border border-gray-300 bg-white pl-7 pr-2 py-1 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-indigo-500"
    />
  </div>
);

const InfoCircle = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z"
      clipRule="evenodd"
    />
  </svg>
);

export default VqqFiltersBar;
